import React, { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import * as S from '@/components/Ad/Status/PaymentFlow.styles';
import { formatNumber } from '@/utils/helpers';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { useAdDetails } from '@/hooks/useAdDetails';
import {
  RestoredAdPopupRef,
  RestoredAdPopupWithRef,
} from '@/components/Ad/Status/RestoredAdPopup';
import { getVariants } from '@/components/Ad/Status/PaymentFlow.constants';
import {
  findMinPricePerAd,
  mapVariantTypeToAdKindOrNull,
} from '@/components/Ad/Status/PaymentFlow.helpers';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { AdStatus } from '@/types';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';
import { useResponsive } from '@/hooks/useResponsive';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { ROUTES } from '@/constants/routes';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { Languages } from '@/constants/common';
import { ModalSheet } from '@/components/UI/ModalSheet';

const PromotionFlow = dynamic(
  () =>
    import('@/components/Ad/Promotions/PromotionFlow').then(
      mod => mod.PromotionFlow
    ),
  { ssr: false }
);

interface PaymentFlowProps {
  isOpen: boolean;
  toggle: (state?: boolean) => void;
}

export const PaymentFlow: React.FC<PaymentFlowProps> = ({ isOpen, toggle }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [isPaymentModalOpen, togglePaymentModal] = useToggleOpenState();
  const restoredAdPopupRef = useRef<RestoredAdPopupRef>(null);
  const { categoryDetails } = useCurrentCategoryDetails();
  const { adDetails } = useAdDetails();
  const isSingleBundle = categoryDetails?.bundles.length === 1;
  const minBundlePrice = findMinPricePerAd(categoryDetails?.bundles);
  const minVipPrice = adDetails?.vipServices[0].price || 0;
  const minFeaturedPrice = adDetails?.featuredServices[0].price || 0;
  const prevVipPrice = adDetails?.vipServices[0].previousPrice || 0;
  const prevFeaturedPrice = adDetails?.featuredServices[0].previousPrice || 0;
  const variants = getVariants({
    t,
    minBundlePrice,
    minVipPrice,
    minFeaturedPrice,
    prevVipPrice,
    prevFeaturedPrice,
  });
  const [selectedVariant, setSelectedVariant] = useState(variants[0]);
  const { isDesktop } = useResponsive();

  function getPrice(price: number) {
    return t(`pages.ad.price_from${isSingleBundle ? '_single' : ''}`, {
      price: formatNumber(price, 2),
    });
  }

  const handleClose = () => {
    togglePaymentModal(false);
    toggle(false);

    if (
      selectedVariant.type === 'bundle' &&
      adDetails?.status === AdStatus.Expired
    ) {
      restoredAdPopupRef.current?.toggle();
    }
  };

  return (
    <>
      <ModalSheet
        dataTestId={AdPageTestIds.paymentFlow.window}
        isOpen={isOpen}
        onClose={() => toggle(false)}
        title={t('pages.ad.ad_for_30_days')}
      >
        <S.Box>
          <S.Subtitle $variant="1">
            {t('pages.ad.limit_in_category', {
              categoryName: categoryDetails?.name,
            })}
          </S.Subtitle>

          {variants.map(variant => {
            const isSelected = variant.id === selectedVariant.id;
            const prevPrice = variant.previousPrice;

            return (
              <S.Variant
                key={variant.id}
                onChangeHook={() => setSelectedVariant(variant)}
                customContent={
                  <>
                    <S.VariantIconBox>{variant.icon}</S.VariantIconBox>
                    <S.VariantTexts>
                      <Typography.Header $variant="3">
                        {variant.title}
                      </Typography.Header>
                      {prevPrice ? (
                        <S.MultiplePrices>
                          {language === Languages.Ru &&
                            !isSingleBundle &&
                            t('common.from')}
                          <S.PrevPrice>
                            {formatNumber(prevPrice, 2)}
                          </S.PrevPrice>
                          <S.NewPrice>
                            {formatNumber(variant.price, 2)}
                            {language === Languages.Az && !isSingleBundle
                              ? ' ₼-dan'
                              : ' ₼'}
                          </S.NewPrice>
                        </S.MultiplePrices>
                      ) : (
                        <Typography.Body $variant="1">
                          {getPrice(variant.price)}
                        </Typography.Body>
                      )}
                    </S.VariantTexts>
                  </>
                }
                value={isSelected}
                appearance="radio"
                size="md"
                color="primary"
                $isActive={isSelected}
                $type={variant.type}
              />
            );
          })}

          <S.ContinueButton
            data-testid={AdPageTestIds.paymentFlow.continueButton}
            appearance="primary"
            isFullWidth
            onClick={togglePaymentModal}
          >
            {t('common.continue')}
          </S.ContinueButton>

          <S.NeedMoreInfo>
            <Trans
              i18nKey="pages.ad.need_more_ads_business_cta"
              components={{
                link1: (
                  <CustomLink
                    href={ROUTES.businessLanding}
                    {...(!isDesktop && { target: '_blank' })}
                  />
                ),
              }}
            />
          </S.NeedMoreInfo>
        </S.Box>
      </ModalSheet>

      <PromotionFlow
        canGoBack={selectedVariant.type === 'bundle'}
        dataTestId={AdPageTestIds.paymentFlow.promotionFlow}
        isOpen={isPaymentModalOpen}
        toggle={togglePaymentModal}
        selectedVariant={selectedVariant}
        type={mapVariantTypeToAdKindOrNull(selectedVariant.type)}
        onSuccessPayment={handleClose}
        {...(isSingleBundle && { minBundlePrice })}
      />

      <RestoredAdPopupWithRef
        ref={restoredAdPopupRef}
        dataTestId={AdPageTestIds.paymentFlow.restoredAdPopup}
      />
    </>
  );
};
